import { useRef, useEffect, useState } from 'react';
import EditorJS from '@editorjs/editorjs';
import { useToast } from '@/hooks/use-toast';
import { articleBlockService } from '@/services/article/articleBlockService';
import { patchEditorJS, calculateReadingStats } from './utils';
import { useEditorConfig } from './useEditorConfig';
import { UseEditorJSOptions, UseEditorJSReturn } from './types';

// Apply the patch on import
try {
  patchEditorJS();
} catch (err) {
  console.warn('Could not patch EditorJS keyboard handling:', err);
}

// Debounce function to prevent excessive saves
const debounce = (fn: Function, ms = 1000) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function(this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const useEditorJS = ({ 
  initialData, 
  onChange, 
  holderRef,
  articleId
}: UseEditorJSOptions): UseEditorJSReturn => {
  const editorRef = useRef<EditorJS | null>(null);
  const editorReadyRef = useRef<boolean>(false);
  const editorSavingRef = useRef<boolean>(false);
  const { toast } = useToast();
  const [wordCount, setWordCount] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const [editorReady, setEditorReady] = useState(false);
  const { getEditorConfig } = useEditorConfig();

  // Create a debounced version of the block update function
  const debouncedSaveBlocks = useRef(
    debounce(async (editorData: any) => {
      if (!articleId || editorSavingRef.current) return;
      
      try {
        editorSavingRef.current = true;
        console.log("Saving blocks to database...");
        
        if (articleId) {
          await articleBlockService.createBlocksFromEditorData(articleId, editorData);
        }
        
        console.log("Blocks saved successfully");
      } catch (error) {
        console.error('Error saving blocks:', error);
        toast({
          title: "Save Error",
          description: "Failed to save content blocks. Your changes may not be saved.",
          variant: "destructive"
        });
      } finally {
        editorSavingRef.current = false;
      }
    }, 2000)  // 2 second debounce
  ).current;

  const initEditor = async () => {
    if (!holderRef.current) return;
    
    try {
      if (editorRef.current) {
        if (editorReadyRef.current && typeof editorRef.current.destroy === 'function') {
          try {
            await editorRef.current.destroy();
          } catch (err) {
            console.error('Error destroying editor:', err);
          }
        }
        editorRef.current = null;
        editorReadyRef.current = false;
        setEditorReady(false);
      }

      const editorConfig = getEditorConfig();
      
      const editor = new EditorJS({
        holder: holderRef.current,
        ...editorConfig,
        data: initialData?.blocks ? initialData : { blocks: [] },
        placeholder: 'Start writing your article here...',
        autofocus: true,
        onChange: async () => {
          if (editorRef.current && editorReadyRef.current) {
            try {
              const data = await editorRef.current.save();
              
              // Calculate statistics without saving
              const { wordCount, readingTime } = calculateReadingStats(data.blocks);
              setWordCount(wordCount);
              setReadingTime(readingTime);
              
              // Don't auto-save blocks directly in onChange to prevent race conditions
              // Instead, call the parent onChange handler which can manually trigger saving
              if (onChange) {
                onChange(data);
              }
              
              // If articleId is provided, use the debounced save function
              // This won't trigger immediate saves on every keystroke
              if (articleId) {
                debouncedSaveBlocks(data);
              }
            } catch (error) {
              console.error('Error saving editor data:', error);
            }
          }
        }
      });

      editor.isReady
        .then(() => {
          editorRef.current = editor;
          editorReadyRef.current = true;
          setEditorReady(true);
          console.log('Editor.js is ready');
          
          // Calculate initial word count and reading time
          if (initialData?.blocks) {
            const { wordCount, readingTime } = calculateReadingStats(initialData.blocks);
            setWordCount(wordCount);
            setReadingTime(readingTime);
          }
        })
        .catch((error) => {
          console.error('Editor.js initialization failed:', error);
          toast({
            title: "Editor Error",
            description: "Failed to initialize the editor. Please refresh the page.",
            variant: "destructive"
          });
        });
    } catch (error) {
      console.error('Error initializing editor:', error);
      toast({
        title: "Editor Error",
        description: "There was a problem initializing the editor. Please try again.",
        variant: "destructive"
      });
    }
  };

  const saveContent = async () => {
    if (!editorRef.current || !editorReadyRef.current) {
      console.error('Editor is not ready for saving');
      return null;
    }
    
    try {
      console.log('Saving editor content...');
      const savedData = await editorRef.current.save();
      console.log('Editor content saved successfully:', savedData);
      return savedData;
    } catch (error) {
      console.error('Error saving editor content:', error);
      toast({
        title: "Save Error",
        description: "Failed to save content. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  const insertBlock = () => {
    if (editorRef.current && editorReadyRef.current) {
      // Get the total number of blocks to append at the end
      const totalBlocks = editorRef.current.blocks.getBlocksCount();
      
      // Insert a new block at the end (after the last block)
      editorRef.current.blocks.insert(undefined, undefined, undefined, totalBlocks, true);
      
      // Scroll to the bottom to show the new block
      setTimeout(() => {
        if (holderRef.current) {
          holderRef.current.scrollTo({
            top: holderRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  const destroyEditor = () => {
    if (editorRef.current && editorReadyRef.current) {
      try {
        editorRef.current.destroy();
      } catch (err) {
        console.error('Error during editor cleanup:', err);
      }
    }
  };

  return {
    initEditor,
    saveContent,
    insertBlock,
    destroyEditor,
    wordCount,
    readingTime,
    editorReady,
  };
};
